'use client';

import type { ReactNode } from 'react';
import CommonSpacer from '@/components/sections/shared/CommonSpacer';
import WebVitals from '@/components/performance/WebVitals';
import SustainabilityHeroEnquiry from '@/components/sections/Sustainability/SustainabilityHeroEnquiry';
import SustainabilityStanQuoteSection from '@/components/sections/Sustainability/SustainabilityStanQuoteSection';
import SustainabilityPillarCards from '@/components/sections/Sustainability/SustainabilityPillarCards';
import SustainabilityStandSection from '@/components/sections/Sustainability/SustainabilityStandSection';
import SustainabilitySDGsSection from '@/components/sections/Sustainability/SustainabilitySDGsSection';
import SustainabilityCommitmentsSection from '@/components/sections/Sustainability/SustainabilityCommitmentsSection';
import type {
  TransformedSustainabilityData,
  SustainabilityComponent,
} from '@/types/sustainability';

interface SustainabilityLandingProps {
  locale: string;
  landingData?: TransformedSustainabilityData | null;
  /**
   * Streaming slot for the FAQ + enquiry block — rendered by an async
   * Server Component wrapped in `<Suspense>` in `page.tsx`.
   */
  faqSlot?: ReactNode;
}

export default function SustainabilityLanding({
  locale,
  landingData,
  faqSlot,
}: SustainabilityLandingProps) {
  const components = landingData?.components ?? [];

  const renderComponentsInOrder = () => {
    const rendered: ReactNode[] = [];
    let sawFaqWithForm = false;

    for (let i = 0; i < components.length; ) {
      const component = components[i];

      switch (component.__component) {
        case 'common.spacer-top':
        case 'common.spacer-full':
        case 'common.spacer-bottom': {
          rendered.push(
            <CommonSpacer key={`${component.__component}-${component.id}`} zone={component} />,
          );
          i += 1;
          break;
        }

        case 'sustainability.section-with-testimonial': {
          rendered.push(
            <SustainabilityStanQuoteSection
              key={`testimonial-${component.id}`}
              section={component}
            />,
          );
          i += 1;
          break;
        }

        case 'sustainability.left-right-section': {
          const standSections: SustainabilityComponent[] = [];
          while (
            i < components.length &&
            components[i].__component === 'sustainability.left-right-section'
          ) {
            standSections.push(components[i]);
            i += 1;
          }

          rendered.push(
            <SustainabilityStandSection
              key={`stands-${standSections.map((s) => (s as any).id).join('-')}`}
              sections={standSections as any}
              locale={locale}
            />,
          );
          break;
        }

        case 'homepage.colorful-card-wrapper': {
          rendered.push(
            <SustainabilitySDGsSection
              key={`sdg-${component.id}`}
              cardWrapper={component as any}
            />,
          );
          i += 1;
          break;
        }

        case 'sustainability.commitments': {
          rendered.push(
            <SustainabilityCommitmentsSection
              key={`commitments-${component.id}`}
              commitments={component as any}
            />,
          );
          i += 1;
          break;
        }

        case 'modules.faqwith-form': {
          if (faqSlot) {
            sawFaqWithForm = true;
            rendered.push(<div key={`faq-${component.id}`}>{faqSlot}</div>);
          }
          i += 1;
          break;
        }

        default:
          i += 1;
      }
    }

    if (faqSlot && !sawFaqWithForm) {
      rendered.push(<div key="faq-fallback">{faqSlot}</div>);
    }

    return rendered;
  };

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-black">
      <WebVitals />
      <SustainabilityHeroEnquiry hero={landingData?.hero} />
      <SustainabilityPillarCards locale={locale} cards={landingData?.redirectionCards} />
      {renderComponentsInOrder()}
    </div>
  );
}
